import {
	Culture,
	cultureLabels,
	UpdateVolunteersHighlightLocalizable,
	UpdateVolunteersHighlightRequest,
	VolunteersHighlightFormData,
	VolunteersHighlightRules,
} from "../context/VolunteersHighlightContext/constants";
import { countText } from "../utils/textLength";

const getCultureLabel = (culture: string) => cultureLabels[culture as Culture] ?? culture;

// Sent as multipart form data: the backend counts every line break as \r\n.
const isTextInvalid = (value: string, maxLength: number) => {
	const trimmed = value.trim();
	return countText(trimmed, "crlf") > maxLength || !VolunteersHighlightRules.textPattern.test(trimmed);
};

const isImageInvalid = (image: File) =>
	image.size > VolunteersHighlightRules.imageMaxFileSize
	|| !(VolunteersHighlightRules.imageMimeTypes as readonly string[]).includes(image.type);

// undefined: not set (add) or unchanged (update); null: a filled value was cleared on the update form.
const validateDisplayOrder = (displayOrder?: number | null) => {
	if (displayOrder === undefined) {
		return null;
	}
	if (displayOrder === null) {
		return "Sıra numarası boş bırakılamaz.";
	}
	return Number.isInteger(displayOrder) && displayOrder >= 1 ? null : "Sıra numarası 1 veya daha büyük bir tam sayı olmalıdır.";
};

const validateTitle = (label: string, title: string) =>
	isTextInvalid(title, VolunteersHighlightRules.titleMaxLength)
		? `${label} başlık en fazla ${VolunteersHighlightRules.titleMaxLength} karakter olmalı ve geçersiz karakter içermemelidir.`
		: null;

const validateDescription = (label: string, description: string) =>
	isTextInvalid(description, VolunteersHighlightRules.descriptionMaxLength)
		? `${label} açıklama en fazla ${VolunteersHighlightRules.descriptionMaxLength} karakter olmalı ve geçersiz karakter içermemelidir.`
		: null;

const validateImage = (label: string, image: File) =>
	isImageInvalid(image) ? `${label} öne çıkan resmi PNG, JPEG veya JPG formatında ve en fazla 20 MB olmalıdır.` : null;

// A culture that is created from scratch (add form, or a culture missing on the server) needs every field.
const validateCompleteLocalizable = (culture: string, title = "", description = "", image?: File | null) => {
	const label = getCultureLabel(culture);

	if (!title.trim()) {
		return `Lütfen ${label} başlık alanını doldurun.`;
	}
	if (!description.trim()) {
		return `Lütfen ${label} açıklama alanını doldurun.`;
	}
	if (!image) {
		return `Lütfen ${label} öne çıkan resmini yükleyin.`;
	}

	return validateTitle(label, title) ?? validateDescription(label, description) ?? validateImage(label, image);
};

// An existing culture is patched partially: only the fields that changed are present.
const validateChangedLocalizable = ({ culture, title, description, image }: UpdateVolunteersHighlightLocalizable) => {
	const label = getCultureLabel(culture);

	if (title !== undefined) {
		const titleError = title.trim() ? validateTitle(label, title) : `${label} başlık boş bırakılamaz.`;
		if (titleError) {
			return titleError;
		}
	}
	if (description !== undefined) {
		const descriptionError = description.trim() ? validateDescription(label, description) : `${label} açıklama boş bırakılamaz.`;
		if (descriptionError) {
			return descriptionError;
		}
	}

	return image ? validateImage(label, image) : null;
};

export class VolunteersHighlightValidator {
	static validateAddVolunteersHighlight = (data: VolunteersHighlightFormData): string | null => {
		const displayOrderError = validateDisplayOrder(data.displayOrder);
		if (displayOrderError) {
			return displayOrderError;
		}

		for (const localizable of data.volunteersHighlightLocalizable) {
			const error = validateCompleteLocalizable(localizable.culture, localizable.title, localizable.description, localizable.image);
			if (error) {
				return error;
			}
		}

		return null;
	};

	// The request only carries changed fields, so an empty value here means the user cleared a filled field.
	static validateUpdateVolunteersHighlight = (data: UpdateVolunteersHighlightRequest): string | null => {
		const displayOrderError = validateDisplayOrder(data.displayOrder);
		if (displayOrderError) {
			return displayOrderError;
		}

		for (const localizable of data.volunteersHighlightLocalizable ?? []) {
			const error = localizable.highlightLocalizableId
				? validateChangedLocalizable(localizable)
				: validateCompleteLocalizable(localizable.culture, localizable.title, localizable.description, localizable.image);
			if (error) {
				return error;
			}
		}

		return null;
	};
}
